import type { useErrorToast } from "@v2/hooks/useErrorToast";
import type {
  Product,
  ProductFormSchema,
  ProductMediaItem,
} from "schemas/generated/product";

import * as React from "react";

import { formatPrice } from "replo-utils/lib/math";
import { isEmpty } from "replo-utils/lib/misc";
import { MAX_FILE_UPLOAD_COUNT } from "schemas/asset";

interface FileRejection {
  file: File;
  errors: Array<{ code: string; message: string }>;
}

export type FileRejections = FileRejection[];

/**
 * Converts a product object to the format expected by the product form.
 */
export const getProductFormData = (product: Product): ProductFormSchema => {
  // NOTE (Kurt, 2025-09-02): We only use the first variant for now because we only
  // support one variant per product.
  const firstVariant = product.variants[0];

  // Convert product media to ProductMediaItem format
  const productMedia: ProductMediaItem[] =
    product.media
      ?.map((item, index) => ({
        assetId: item.id,
        url: item.type === "image" ? item.url : item.previewImage?.url ?? "",
        sortOrder: item.sortOrder ?? index,
        type: item.type === "image" ? ("image" as const) : ("video" as const),
      }))
      .filter((item) => item.url) ?? [];

  // Convert product files to form format
  const productFiles =
    product.productFiles?.map(
      ({ id, url, name, sizeBytes, fileExtension, label }) => ({
        id,
        url,
        name,
        sizeBytes,
        fileExtension,
        label: label ?? undefined,
      }),
    ) ?? [];

  // Convert productEmail to form format (transform null to undefined)
  const productEmail = product.productEmails?.[0]
    ? {
        header: product.productEmails[0].header,
        message: product.productEmails[0].message ?? undefined,
        buttonText: product.productEmails[0].buttonText ?? undefined,
        buttonLink: product.productEmails[0].buttonLink ?? undefined,
      }
    : undefined;

  return {
    name: product.title,
    productType: product.type ?? "UNASSIGNED",
    price: firstVariant?.price ?? 0,
    compareAtPrice: firstVariant?.originalPrice ?? null,
    description: product.description,
    checkoutMethod:
      product.externalCheckoutUrl && product.externalCheckoutUrl.length > 0
        ? "external"
        : "replo",
    externalCheckoutUrl: product.externalCheckoutUrl ?? null,
    images: [],
    productFiles,
    media: productMedia,
    productEmail,
    hasFulfillmentEmail: Boolean(product.productEmails?.[0]),
  };
};

export const getColorClasses = (color: string) => {
  const colorMap = {
    pink: {
      border: "border-pink",
      background: "bg-pink-soft",
      iconColor: "text-pink",
      iconBg: "bg-pink-soft",
      iconBorder: "border-pink-border",
    },
    violet: {
      border: "border-violet",
      background: "bg-violet-soft",
      iconColor: "text-violet",
      iconBg: "bg-violet-soft",
      iconBorder: "border-violet-border",
    },
    lime: {
      border: "border-lime",
      background: "bg-lime-soft",
      iconColor: "text-lime",
      iconBg: "bg-lime-soft",
      iconBorder: "border-lime-border",
    },
    orange: {
      border: "border-orange",
      background: "bg-orange-soft",
      iconColor: "text-orange",
      iconBg: "bg-orange-soft",
      iconBorder: "border-orange-border",
    },
    teal: {
      border: "border-teal",
      background: "bg-teal-soft",
      iconColor: "text-teal",
      iconBg: "bg-teal-soft",
      iconBorder: "border-teal-border",
    },
    sky: {
      border: "border-sky",
      background: "bg-sky-soft",
      iconColor: "text-sky",
      iconBg: "bg-sky-soft",
      iconBorder: "border-sky-border",
    },
    fuchsia: {
      border: "border-fuchsia",
      background: "bg-fuchsia-soft",
      iconColor: "text-fuchsia",
      iconBg: "bg-fuchsia-soft",
      iconBorder: "border-fuchsia-border",
    },
    yellow: {
      border: "border-yellow",
      background: "bg-yellow-soft",
      iconColor: "text-yellow",
      iconBg: "bg-yellow-soft",
      iconBorder: "border-yellow-border",
    },
    neutral: {
      border: "border-neutral",
      background: "bg-neutral-soft",
      iconColor: "text-neutral",
      iconBg: "bg-neutral-soft",
      iconBorder: "border-neutral-border",
    },
  };
  return colorMap[color as keyof typeof colorMap] ?? colorMap.neutral;
};

/**
 * Truncates a string in the middle, preserving the file extension if present.
 * Useful for displaying long file names in a compact format.
 */
export const truncateMiddle = (
  text: string,
  maxLength: number = 40,
): string => {
  if (text.length <= maxLength) {
    return text;
  }

  const lastDotIndex = text.lastIndexOf(".");
  const extension =
    lastDotIndex !== -1 ? text.slice(Math.max(0, lastDotIndex)) : "";
  const nameWithoutExtension =
    lastDotIndex !== -1 ? text.slice(0, Math.max(0, lastDotIndex)) : text;

  const availableLength = maxLength - extension.length - 3;

  if (availableLength <= 0) {
    return `...${extension}`;
  }

  const startLength = Math.ceil(availableLength / 2);
  const endLength = Math.floor(availableLength / 2);

  const start = nameWithoutExtension.slice(0, Math.max(0, startLength));
  const end = nameWithoutExtension.slice(
    Math.max(0, nameWithoutExtension.length - endLength),
  );

  return `${start}...${end}${extension}`;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

type AcceptedAssetTypes = ("image" | "video")[];

function getAssetTypeLabel(acceptedTypes?: AcceptedAssetTypes): string {
  if (!acceptedTypes || acceptedTypes.length === 0) {
    return "file";
  }
  if (acceptedTypes.length === 1) {
    return acceptedTypes[0] ?? "file";
  }
  return "image or video";
}

export function handleFileRejections(
  fileRejections: FileRejections,
  errorToast: ReturnType<typeof useErrorToast>,
  acceptedTypes?: AcceptedAssetTypes,
): boolean {
  if (isEmpty(fileRejections)) {
    return false;
  }

  const typeLabel = getAssetTypeLabel(acceptedTypes);
  const typeLabelPlural = typeLabel === "file" ? "files" : `${typeLabel}s`;

  if (
    fileRejections.find((fileRejection) => {
      return fileRejection.errors.find(
        (error) => error.code === "too-many-files",
      );
    })
  ) {
    errorToast(
      "Files Exceeding Limit",
      `Too many files selected. Please upload up to ${MAX_FILE_UPLOAD_COUNT} files at a time for a successful upload.`,
      "error.asset.upload.too_many_files",
    );
    return true;
  }

  const invalidTypeRejections = fileRejections.filter((rejection) => {
    return rejection.errors.some((error) => error.code === "file-invalid-type");
  });

  if (invalidTypeRejections.length > 0) {
    const invalidCount = invalidTypeRejections.length;
    const invalidPlural = invalidCount > 1;
    const fileNames = invalidTypeRejections.map((r) => r.file.name);
    const fileNamesList =
      fileNames.length <= 3
        ? fileNames.join(", ")
        : `${fileNames.slice(0, 3).join(", ")} and ${fileNames.length - 3} more`;

    errorToast(
      invalidPlural ? `Invalid ${typeLabelPlural}` : `Invalid ${typeLabel}`,
      invalidPlural
        ? `The following ${typeLabelPlural} are not supported: ${fileNamesList}`
        : `"${fileNames[0]}" is not a supported ${typeLabel} type.`,
      "error.asset.upload.invalid_file_type",
    );
  }

  const tooLargeRejections = fileRejections.filter((rejection) => {
    return rejection.errors.some((error) => {
      return (
        error.code === "file-too-large" || error.code === "replo-file-too-large"
      );
    });
  });

  if (tooLargeRejections.length > 0) {
    const largeCount = tooLargeRejections.length;
    const largePlural = largeCount > 1;
    const fileNames = tooLargeRejections.map((r) => r.file.name);
    const fileNamesList =
      fileNames.length <= 3
        ? fileNames.join(", ")
        : `${fileNames.slice(0, 3).join(", ")} and ${fileNames.length - 3} more`;

    errorToast(
      largePlural ? "Files Too Large" : "File Too Large",
      largePlural
        ? `The following files exceed the size limit: ${fileNamesList}`
        : `"${fileNames[0]}" exceeds the size limit.`,
      "error.asset.upload.file_too_large",
    );
  }

  return false;
}

export const STATUS_CONFIGS = {
  active: {
    iconClass: "bg-positive-default",
    badgeClass: "bg-emerald-soft text-emerald-default",
  },
  draft: {
    iconClass: "bg-warning-default",
    badgeClass: "bg-warning-soft text-warning-default",
  },
  archived: {
    iconClass: "bg-component-subtle",
    badgeClass: "bg-component-subtle text-foreground-muted",
  },
} as const;

export const getStatusBadgeClasses = (status: Product["status"]): string => {
  const config = STATUS_CONFIGS[status as keyof typeof STATUS_CONFIGS];
  return config?.badgeClass ?? "bg-component text-foreground";
};

const formatPriceRange = (minPrice: number, maxPrice: number): string => {
  return minPrice === maxPrice
    ? formatPrice(minPrice)
    : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
};

export const getPriceRange = (variants: Product["variants"]) => {
  if (!variants?.length) {
    return "No price";
  }

  const validPrices = variants
    .map((v) => v.price)
    .filter((price): price is number => typeof price === "number");

  if (!validPrices.length) {
    return "No price";
  }

  const minPrice = Math.min(...validPrices);
  const maxPrice = Math.max(...validPrices);

  return formatPriceRange(minPrice, maxPrice);
};

export const openShopifyProductPageUrl = (shopifyUrl: string) => {
  const shopName = shopifyUrl.replace(".myshopify.com", "");
  window.open(`https://admin.shopify.com/store/${shopName}/products`, "_blank");
};
